import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Badge, Button, EmptyState, Field, KeyValue, Select, Spinner } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { Table, td, th } from '../components/reports/blocks';
import { triggerDefaults, WorkflowBuilder, type Def } from '../components/workflows/builder';
import { FLOW_TONE, RUN_TONE, STEP_TONE, useWorkflowText } from '../components/workflows/labels';
import { api, ApiError, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  WorkflowCatalog,
  WorkflowDetail,
  WorkflowList,
  WorkflowPauseAll,
  WorkflowRunDetail,
  WorkflowRunList,
  WorkflowTest,
} from '../lib/types-faz8';

/**
 * Görsel otomasyon kurucusu (F8-15, ADR-816): akış listesi ve hazır örnekler, Tetikleyici → Koşul → Eylem
 * düzenleyicisi, kuru deneme, açma/durdurma/arşiv, çalışma kaydı ve adım ayrıntısı. Rehber:
 * docs/faz-8/REPORTS_FINANCE_TR.md#otomasyon-akislari.
 */

const useCatalog = () =>
  useQuery({
    queryKey: ['workflow-catalog'],
    queryFn: () => api.get<WorkflowCatalog>('/v1/admin/workflows/catalog'),
  });

function ErrorText({ error }: { error: unknown }) {
  return (
    <p role="alert" className="text-sm text-rose">
      {error instanceof ApiError ? error.title : String(error)}
    </p>
  );
}

// ------------------------------------------------------------------------------------------ Liste

export function WorkflowsPage() {
  const w = useWorkflowText();
  const { t } = w;
  const { can } = usePermissions();
  const manage = can('workflows.manage');
  const navigate = useNavigate();
  const { formatDate } = useI18n();
  const list = useQuery({
    queryKey: ['workflows'],
    queryFn: () => api.get<WorkflowList>('/v1/admin/workflows'),
  });
  const catalog = useCatalog();
  const runs = useQuery({
    queryKey: ['workflow-runs', 'all'],
    queryFn: () => api.get<WorkflowRunList>('/v1/admin/workflows/runs?limit=20'),
  });
  const [idem] = useState(() => new IdempotencyKey());
  const fromTemplate = useApiMutation(
    (k: string) =>
      api.post<WorkflowDetail>(
        '/v1/admin/workflows/from-template',
        { templateKey: k, name: t(`admin.workflows.templates.${k}.name`) },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['workflows']],
      onSuccess: (r) => void navigate({ to: '/otomasyon-akislari/$id', params: { id: r.id } }),
    },
  );
  const [blankIdem] = useState(() => new IdempotencyKey());
  const blank = useApiMutation(
    () =>
      api.post<WorkflowDetail>(
        '/v1/admin/workflows',
        {
          name: t('admin.workflows.newFlowName'),
          trigger: { type: 'product.created', params: triggerDefaults('product.created') },
        },
        { idempotencyKey: blankIdem },
      ),
    {
      invalidate: [['workflows']],
      onSuccess: (r) => void navigate({ to: '/otomasyon-akislari/$id', params: { id: r.id } }),
    },
  );
  const pauseAll = useApiMutation(() => api.post<WorkflowPauseAll>('/v1/admin/workflows/pause-all'), {
    invalidate: [['workflows'], ['workflow-runs']],
    success: (r) => t('admin.workflows.pausedAll', { count: r.paused }),
  });
  const rows = list.data ?? [];
  const active = rows.filter((r) => r.status === 'active').length;
  const autoPaused = rows.filter((r) => r.status === 'paused' && r.pausedReason !== 'manual');
  const failed = rows.reduce((s, r) => s + (r.runs.failed ?? 0), 0);
  return (
    <PageFrame
      helpKey="page.workflows"
      wide
      actions={
        manage ? (
          <span className="flex flex-wrap gap-2">
            <Button variant="primary" loading={blank.isPending} onClick={() => blank.mutate(undefined)}>
              {t('admin.workflows.newFlow')}
            </Button>
            <ConfirmButton
              variant="secondary"
              size="md"
              disabled={!active}
              message={t('admin.workflows.pauseAllConfirm')}
              onConfirm={() => pauseAll.mutateAsync(undefined)}
            >
              {t('admin.workflows.pauseAll')}
            </ConfirmButton>
            <HelpTip k="workflows.pause_all" />
          </span>
        ) : undefined
      }
    >
      <p className="mb-6 max-w-[75ch] text-sm text-ink-2">{t('admin.workflows.lead')}</p>
      {autoPaused.length > 0 && (
        <div role="alert" className="mb-6 rounded-md border border-amber bg-amber/10 p-3 text-sm text-ink">
          {t('admin.workflows.autoPausedAlert', { count: autoPaused.length })}{' '}
          {autoPaused.map((r) => (
            <Link
              key={r.id}
              to="/otomasyon-akislari/$id"
              params={{ id: r.id }}
              className="me-2 font-medium text-pine hover:underline"
            >
              {r.name}
            </Link>
          ))}
        </div>
      )}
      <section aria-labelledby="flows-title" className="mb-10">
        <h2 id="flows-title" className="mb-1 text-lg font-semibold text-ink">
          {t('admin.workflows.flowsTitle')}
        </h2>
        <p className="mb-3 text-sm text-ink-3">
          {t('admin.workflows.summaryLine', {
            active,
            total: rows.filter((r) => r.status !== 'archived').length,
            failed,
          })}
        </p>
        {list.isError ? (
          <ErrorText error={list.error} />
        ) : !list.data ? (
          <Spinner label={t('common.loading')} />
        ) : !rows.length ? (
          <EmptyState title={t('admin.workflows.flowsEmpty')} />
        ) : (
          <Table
            caption={t('admin.workflows.flowsTitle')}
            head={
              <tr>
                <th scope="col" className={th}>
                  {t('admin.workflows.columns.name')}
                </th>
                <th scope="col" className={th}>
                  {t('admin.workflows.columns.chain')}
                </th>
                <th scope="col" className={th}>
                  {t('admin.workflows.columns.status')}
                </th>
                <th scope="col" className={th}>
                  {t('admin.workflows.columns.runs')}
                </th>
                <th scope="col" className={th}>
                  {t('admin.workflows.columns.last')}
                </th>
              </tr>
            }
          >
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-line">
                <th scope="row" className={`${td} text-start font-medium`}>
                  <Link
                    to="/otomasyon-akislari/$id"
                    params={{ id: r.id }}
                    className="text-pine hover:underline"
                  >
                    {r.name}
                  </Link>
                  {r.runAsName && (
                    <p className="text-xs font-normal text-ink-3">
                      {t('admin.workflows.runAsShort', { name: r.runAsName })}
                    </p>
                  )}
                </th>
                <td className={td}>
                  {w.trigger(r.triggerType)}
                  <span className="text-ink-3"> → </span>
                  {r.actionTypes.length ? r.actionTypes.map(w.action).join(' → ') : '—'}
                </td>
                <td className={td}>
                  <Badge tone={FLOW_TONE[r.status] ?? 'neutral'}>
                    {t(`admin.workflows.statuses.${r.status}`)}
                  </Badge>
                  {r.status === 'paused' && r.pausedReason && (
                    <p className="mt-1 text-xs text-ink-3">
                      {t(`admin.workflows.pausedReasons.${r.pausedReason}`)}
                    </p>
                  )}
                </td>
                <td className={td}>
                  {t('admin.workflows.runsCounts', {
                    succeeded: r.runs.succeeded ?? 0,
                    failed: r.runs.failed ?? 0,
                    pending: (r.runs.pending ?? 0) + (r.runs.running ?? 0),
                  })}
                </td>
                <td className={td}>
                  {r.lastRunAt ? formatDate(r.lastRunAt) : t('admin.workflows.never')}
                  {r.nextAt && (
                    <p className="text-xs text-ink-3">
                      {t('admin.workflows.next', { date: formatDate(r.nextAt) })}
                    </p>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </section>

      <section aria-labelledby="templates-title" className="mb-10">
        <h2 id="templates-title" className="mb-3 flex items-center gap-1 text-lg font-semibold text-ink">
          {t('admin.workflows.templatesTitle')}
          <HelpTip k="workflows.templates" />
        </h2>
        {catalog.isError ? (
          <ErrorText error={catalog.error} />
        ) : !catalog.data ? (
          <Spinner label={t('common.loading')} />
        ) : (
          <ul className="grid gap-3 md:grid-cols-2">
            {catalog.data.templates.map((tp) => (
              <li key={tp.key} className="flex flex-col rounded-md border border-line bg-surface p-4">
                <p className="font-medium text-ink">{t(`admin.workflows.templates.${tp.key}.name`)}</p>
                <p className="mt-1 flex-1 text-sm text-ink-2">
                  {t(`admin.workflows.templates.${tp.key}.text`)}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {tp.kind === 'linked' ? (
                    <>
                      <Badge tone="slate">{t('admin.workflows.linked')}</Badge>
                      <Badge tone={tp.enabled ? 'moss' : 'neutral'}>
                        {tp.enabled ? t('admin.workflows.linkedOn') : t('admin.workflows.linkedOff')}
                      </Badge>
                      <Link to={tp.path as '/'} className="text-sm font-medium text-pine hover:underline">
                        {t('admin.workflows.openLinked')}
                      </Link>
                      <HelpTip k="workflows.linked" />
                    </>
                  ) : (
                    manage && (
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={fromTemplate.isPending && fromTemplate.variables === tp.key}
                        aria-label={t('admin.workflows.useTemplateNamed', {
                          name: t(`admin.workflows.templates.${tp.key}.name`),
                        })}
                        onClick={() => fromTemplate.mutate(tp.key)}
                      >
                        {t('admin.workflows.useTemplate')}
                      </Button>
                    )
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-labelledby="recent-title">
        <h2 id="recent-title" className="mb-3 flex items-center gap-1 text-lg font-semibold text-ink">
          {t('admin.workflows.recentRuns')}
          <HelpTip k="workflows.runs" />
        </h2>
        <RunsTable q={runs} showFlow />
      </section>
    </PageFrame>
  );
}

function RunsTable({
  q,
  showFlow,
}: {
  q: { data?: WorkflowRunList; isError: boolean; error: unknown };
  showFlow?: boolean;
}) {
  const w = useWorkflowText();
  const { t } = w;
  const { formatDate } = useI18n();
  if (q.isError) return <ErrorText error={q.error} />;
  if (!q.data) return <Spinner label={t('common.loading')} />;
  if (!q.data.rows.length) return <EmptyState title={t('admin.workflows.runsEmpty')} />;
  return (
    <Table
      caption={t('admin.workflows.runsTitle')}
      head={
        <tr>
          <th scope="col" className={th}>
            {t('admin.workflows.runColumns.event')}
          </th>
          {showFlow && (
            <th scope="col" className={th}>
              {t('admin.workflows.columns.name')}
            </th>
          )}
          <th scope="col" className={th}>
            {t('admin.workflows.runColumns.status')}
          </th>
          <th scope="col" className={th}>
            {t('admin.workflows.runColumns.progress')}
          </th>
          <th scope="col" className={th}>
            {t('admin.workflows.runColumns.when')}
          </th>
        </tr>
      }
    >
      {q.data.rows.map((r) => (
        <tr key={r.id} className="border-t border-line">
          <th scope="row" className={`${td} text-start font-medium`}>
            <Link to="/otomasyon-calismasi/$id" params={{ id: r.id }} className="text-pine hover:underline">
              {r.label}
            </Link>
            <p className="text-xs font-normal text-ink-3">{w.trigger(r.triggerType)}</p>
          </th>
          {showFlow && <td className={td}>{r.workflowName}</td>}
          <td className={td}>
            <Badge tone={RUN_TONE[r.status] ?? 'neutral'}>
              {t(`admin.workflows.runStatuses.${r.status}`)}
            </Badge>
            {r.reason && <p className="mt-1 text-xs text-ink-3">{w.reason(r.reason)}</p>}
            {r.status !== 'skipped' && r.lastError && (
              <p className="mt-1 text-xs text-rose">{w.code(r.lastError)}</p>
            )}
          </td>
          <td className={td}>
            {r.status === 'skipped'
              ? '—'
              : t('admin.workflows.progress', { done: r.stepIndex, total: r.steps })}
          </td>
          <td className={td}>
            {formatDate(r.createdAt)}
            {r.status === 'pending' && r.nextAttemptAt && r.attempts > 0 && (
              <p className="text-xs text-ink-3">
                {t('admin.workflows.nextAttempt', { date: formatDate(r.nextAttemptAt) })}
              </p>
            )}
          </td>
        </tr>
      ))}
    </Table>
  );
}

// ------------------------------------------------------------------------------------------ Akış ayrıntısı

const defOf = (d: WorkflowDetail): Def => ({
  name: d.name,
  description: d.description,
  trigger: d.trigger,
  conditions: d.conditions,
  actions: d.actions,
  throttleMinutes: d.throttleMinutes,
  maxRunsPerHour: d.maxRunsPerHour,
});

export function WorkflowDetailPage() {
  const w = useWorkflowText();
  const { t } = w;
  const { formatDate } = useI18n();
  const { id } = useParams({ from: '/app/otomasyon-akislari/$id' });
  const q = useQuery({
    queryKey: ['workflow', id],
    queryFn: () => api.get<WorkflowDetail>(`/v1/admin/workflows/${id}`),
  });
  const catalog = useCatalog();
  const [def, setDef] = useState<Def | null>(null);
  const [status, setStatus] = useState('');
  const runs = useQuery({
    queryKey: ['workflow-runs', id, status],
    queryFn: () =>
      api.get<WorkflowRunList>(
        `/v1/admin/workflows/runs?workflowId=${id}&limit=50${status ? `&status=${status}` : ''}`,
      ),
    placeholderData: keepPreviousData,
  });
  useEffect(() => {
    if (q.data) setDef(defOf(q.data));
  }, [q.data]);
  const invalidate = [['workflow', id], ['workflows'], ['workflow-runs']];
  const save = useApiMutation(
    () => api.patch<WorkflowDetail>(`/v1/admin/workflows/${id}`, def!, { ifMatch: q.data!.version }),
    { invalidate, success: t('admin.workflows.saved'), silentFieldErrors: true },
  );
  const [actIdem] = useState(() => new IdempotencyKey());
  const activate = useApiMutation(
    () => api.post(`/v1/admin/workflows/${id}/activate`, undefined, { idempotencyKey: actIdem }),
    { invalidate, success: t('admin.workflows.activated'), silentFieldErrors: true },
  );
  const pause = useApiMutation(() => api.post(`/v1/admin/workflows/${id}/pause`), {
    invalidate,
    success: t('admin.workflows.paused'),
  });
  const archive = useApiMutation(() => api.post(`/v1/admin/workflows/${id}/archive`), {
    invalidate,
    success: t('admin.workflows.archived'),
  });
  const test = useApiMutation((d: Def) => api.post<WorkflowTest>('/v1/admin/workflows/test', d));
  if (q.isError || catalog.isError)
    return (
      <PageFrame helpKey="page.workflow">
        <ErrorText error={q.error ?? catalog.error} />
      </PageFrame>
    );
  if (!q.data || !catalog.data || !def)
    return (
      <PageFrame helpKey="page.workflow">
        <Spinner label={t('common.loading')} />
      </PageFrame>
    );
  const d = q.data;
  const dirty = JSON.stringify(def) !== JSON.stringify(defOf(d));
  const readOnly = !d.canManage || d.status === 'archived';
  const errors =
    (save.error instanceof ApiError && save.error.errors.length ? save.error.errors : null) ??
    (activate.error instanceof ApiError && activate.error.errors.length ? activate.error.errors : null);
  const problems = errors ?? (dirty ? [] : d.problems);
  return (
    <PageFrame
      helpKey="page.workflow"
      wide
      title={d.name}
      actions={
        <span className="flex flex-wrap items-center gap-2">
          <Badge tone={FLOW_TONE[d.status] ?? 'neutral'}>{t(`admin.workflows.statuses.${d.status}`)}</Badge>
          {!readOnly && (
            <>
              <Button
                variant="secondary"
                disabled={!dirty}
                loading={save.isPending}
                onClick={() => save.mutate(undefined)}
              >
                {t('admin.workflows.save')}
              </Button>
              {d.status === 'active' ? (
                <Button
                  key="pause"
                  variant="secondary"
                  loading={pause.isPending}
                  onClick={() => pause.mutate(undefined)}
                >
                  {t('admin.workflows.pause')}
                </Button>
              ) : (
                <Button
                  key="activate"
                  variant="primary"
                  disabled={dirty || d.problems.length > 0}
                  loading={activate.isPending}
                  onClick={() => activate.mutate(undefined)}
                >
                  {t('admin.workflows.activate')}
                </Button>
              )}
              {d.status !== 'active' && (
                <ConfirmButton
                  variant="ghost"
                  size="md"
                  message={t('admin.workflows.archiveConfirm')}
                  onConfirm={() => archive.mutateAsync(undefined)}
                >
                  {t('admin.workflows.archive')}
                </ConfirmButton>
              )}
            </>
          )}
        </span>
      }
    >
      <p className="mb-4 text-sm">
        <Link to="/otomasyon-akislari" className="text-pine hover:underline">
          ← {t('admin.workflows.back')}
        </Link>
      </p>
      <div className="mb-4 space-y-1 text-sm text-ink-2">
        {d.status === 'paused' && d.pausedReason && (
          <p className="font-medium text-amber">{t(`admin.workflows.pausedReasons.${d.pausedReason}`)}</p>
        )}
        {d.runAsName && (
          <p>
            {t('admin.workflows.runAs', { name: d.runAsName })} <HelpTip k="workflows.run_as" />
          </p>
        )}
        {d.nextAt && <p>{t('admin.workflows.next', { date: formatDate(d.nextAt) })}</p>}
        {d.status === 'active' && !readOnly && (
          <p className="text-ink-3">{t('admin.workflows.activeEditNote')}</p>
        )}
        {dirty && <p className="text-ink-3">{t('admin.workflows.unsaved')}</p>}
      </div>
      {problems.length > 0 && (
        <div role="alert" className="mb-4 rounded-md border border-amber bg-amber/10 p-3">
          <p className="text-sm font-medium text-ink">{t('admin.workflows.problemsTitle')}</p>
          <ul className="mt-1 list-disc ps-5 text-sm text-ink-2">
            {problems.map((p, i) => (
              <li key={i}>{w.problem(p)}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="grid gap-8 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div>
          <WorkflowBuilder value={def} onChange={setDef} catalog={catalog.data} readOnly={readOnly} />
          {!readOnly && (
            <div className="mt-4">
              <Button variant="secondary" loading={test.isPending} onClick={() => test.mutate(def)}>
                {t('admin.workflows.test')}
              </Button>{' '}
              <HelpTip k="workflows.test" />
              {test.data && <TestResult r={test.data} def={def} />}
            </div>
          )}
        </div>
        <section aria-labelledby="runs-title">
          <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
            <h2 id="runs-title" className="flex items-center gap-1 text-lg font-semibold text-ink">
              {t('admin.workflows.runsTitle')}
              <HelpTip k="workflows.runs" />
            </h2>
            <Field label={t('admin.workflows.statusFilter')} className="w-44">
              {(p) => (
                <Select {...p} value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="">{t('admin.reports.all')}</option>
                  {(['pending', 'running', 'succeeded', 'failed', 'skipped', 'cancelled'] as const).map(
                    (s) => (
                      <option key={s} value={s}>
                        {t(`admin.workflows.runStatuses.${s}`)}
                      </option>
                    ),
                  )}
                </Select>
              )}
            </Field>
          </div>
          <RunsTable q={runs} />
        </section>
      </div>
    </PageFrame>
  );
}

function TestResult({ r, def }: { r: WorkflowTest; def: Def }) {
  const w = useWorkflowText();
  const { t } = w;
  const { formatDate } = useI18n();
  return (
    <section aria-label={t('admin.workflows.testTitle')} className="mt-3 rounded-md border border-line p-3">
      <p className="text-sm font-medium text-ink">{t('admin.workflows.testTitle')}</p>
      <p className="text-xs text-ink-3">{t('admin.workflows.testNote')}</p>
      {!r.samples.length ? (
        <p className="mt-2 text-sm text-ink-2">{t('admin.workflows.testEmpty')}</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {r.samples.map((s, i) => (
            <li key={i} className="text-sm">
              <Badge tone={s.matched && !s.outOfScope ? 'moss' : 'neutral'}>
                {s.outOfScope
                  ? t('admin.workflows.outOfScope')
                  : s.matched
                    ? t('admin.workflows.matched')
                    : t('admin.workflows.notMatched')}
              </Badge>{' '}
              <span className="font-medium text-ink">{s.label}</span>{' '}
              <span className="text-ink-3">{formatDate(s.at)}</span>
              {s.results.length > 0 && (
                <span className="block text-xs text-ink-3">
                  {def.conditions.rules
                    .map(
                      (rule, j) =>
                        `${w.field(rule.field)}: ${s.results[j] ? t('admin.workflows.ruleResult.true') : t('admin.workflows.ruleResult.false')}`,
                    )
                    .join(' · ')}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ------------------------------------------------------------------------------------------ Çalışma ayrıntısı

export function WorkflowRunPage() {
  const w = useWorkflowText();
  const { t } = w;
  const { formatDate } = useI18n();
  const money = useMoney();
  const { id } = useParams({ from: '/app/otomasyon-calismasi/$id' });
  const q = useQuery({
    queryKey: ['workflow-run', id],
    queryFn: () => api.get<WorkflowRunDetail>(`/v1/admin/workflows/runs/${id}`),
    refetchInterval: (query) =>
      query.state.data?.status === 'pending' || query.state.data?.status === 'running' ? 5000 : false,
  });
  const invalidate = [['workflow-run', id], ['workflow-runs'], ['workflows']];
  const [retryIdem] = useState(() => new IdempotencyKey());
  const retry = useApiMutation(
    () => api.post(`/v1/admin/workflows/runs/${id}/retry`, undefined, { idempotencyKey: retryIdem }),
    { invalidate, success: t('admin.workflows.retried') },
  );
  const cancel = useApiMutation(() => api.post(`/v1/admin/workflows/runs/${id}/cancel`), {
    invalidate,
    success: t('admin.workflows.cancelledToast'),
  });
  if (q.isError)
    return (
      <PageFrame helpKey="page.workflow_run">
        <ErrorText error={q.error} />
      </PageFrame>
    );
  if (!q.data)
    return (
      <PageFrame helpKey="page.workflow_run">
        <Spinner label={t('common.loading')} />
      </PageFrame>
    );
  const r = q.data;
  const fieldValue = (k: string, v: string | number | boolean | null) => {
    if (v === null || v === '') return '—';
    if (typeof v === 'boolean') return v ? t('admin.workflows.options.yes') : t('admin.workflows.options.no');
    if (k === 'amount' && typeof v === 'number') return money(v, String(r.subject.fields.currency ?? 'TRY'));
    if (
      typeof v === 'string' &&
      ['status', 'source', 'scope', 'channel', 'state', 'kind', 'outcome', 'environment'].includes(k)
    )
      return w.option(v);
    return String(v);
  };
  return (
    <PageFrame
      helpKey="page.workflow_run"
      wide
      title={`${t('admin.workflows.runTitle')}: ${r.subject.label}`}
      actions={
        <span className="flex flex-wrap items-center gap-2">
          <Badge tone={RUN_TONE[r.status] ?? 'neutral'}>{t(`admin.workflows.runStatuses.${r.status}`)}</Badge>
          {r.canRetry && (
            <Button variant="primary" loading={retry.isPending} onClick={() => retry.mutate(undefined)}>
              {t('admin.workflows.retry')}
            </Button>
          )}
          {r.canCancel && (
            <ConfirmButton
              variant="secondary"
              size="md"
              message={t('admin.workflows.cancelConfirm')}
              onConfirm={() => cancel.mutateAsync(undefined)}
            >
              {t('admin.workflows.cancel')}
            </ConfirmButton>
          )}
          <HelpTip k="workflows.retry" />
        </span>
      }
    >
      <p className="mb-4 text-sm">
        <Link
          to="/otomasyon-akislari/$id"
          params={{ id: r.workflowId }}
          className="text-pine hover:underline"
        >
          ← {r.workflowName}
        </Link>
      </p>
      <KeyValue
        items={[
          { label: t('admin.workflows.triggerLabel'), value: w.trigger(r.triggerType) },
          { label: t('admin.workflows.event'), value: r.subject.label },
          { label: t('admin.workflows.createdAt'), value: formatDate(r.createdAt) },
          ...(r.finishedAt
            ? [{ label: t('admin.workflows.finishedAt'), value: formatDate(r.finishedAt) }]
            : []),
          ...(r.reason ? [{ label: t('admin.workflows.reasonLabel'), value: w.reason(r.reason) }] : []),
          ...(r.lastError ? [{ label: t('admin.workflows.lastError'), value: w.code(r.lastError) }] : []),
          ...(r.status === 'pending' && r.nextAttemptAt && r.attempts > 0
            ? [
                {
                  label: t('admin.workflows.attempts'),
                  value: t('admin.workflows.nextAttempt', { date: formatDate(r.nextAttemptAt) }),
                },
              ]
            : []),
          { label: t('admin.workflows.eventKey'), value: <code className="text-xs">{r.eventKey}</code> },
        ]}
      />

      {Object.keys(r.subject.fields).length > 0 && (
        <section aria-labelledby="fields-title" className="mt-8">
          <h2 id="fields-title" className="mb-2 text-lg font-semibold text-ink">
            {t('admin.workflows.fieldsTitle')}
          </h2>
          <KeyValue
            items={Object.entries(r.subject.fields).map(([k, v]) => ({
              label: w.field(k),
              value: fieldValue(k, v),
            }))}
          />
        </section>
      )}

      {r.conditions.rules.length > 0 && (
        <section aria-labelledby="conds-title" className="mt-8">
          <h2 id="conds-title" className="mb-2 text-lg font-semibold text-ink">
            {t('admin.workflows.conditionsTitle')} ({t(`admin.workflows.matches.${r.conditions.match}`)})
          </h2>
          <ul className="space-y-1 text-sm">
            {r.conditions.rules.map((rule, i) => (
              <li key={i}>
                <Badge tone={r.conditionResults[i] ? 'moss' : 'neutral'}>
                  {r.conditionResults[i]
                    ? t('admin.workflows.ruleResult.true')
                    : t('admin.workflows.ruleResult.false')}
                </Badge>{' '}
                {w.field(rule.field)} {t(`admin.workflows.ops.${rule.op}`)}{' '}
                {rule.value === null
                  ? ''
                  : rule.field === 'amount' && typeof rule.value === 'number'
                    ? money(rule.value, 'TRY')
                    : typeof rule.value === 'string' &&
                        [
                          'status',
                          'source',
                          'scope',
                          'channel',
                          'state',
                          'kind',
                          'outcome',
                          'environment',
                        ].includes(rule.field)
                      ? w.option(rule.value)
                      : String(rule.value)}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section aria-labelledby="steps-title" className="mt-8">
        <h2 id="steps-title" className="mb-2 text-lg font-semibold text-ink">
          {t('admin.workflows.stepsTitle')}
        </h2>
        {!r.steps.length ? (
          <p className="text-sm text-ink-3">{t('admin.workflows.stepsEmpty')}</p>
        ) : (
          <ol className="space-y-2">
            {r.steps.map((s, i) => (
              <li key={i} className="rounded-md border border-line bg-surface p-3 text-sm">
                <p className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-ink">
                    {s.stepIndex + 1}. {w.action(s.actionType)}
                  </span>
                  <Badge tone={STEP_TONE[s.outcome] ?? 'neutral'}>
                    {t(`admin.workflows.outcomes.${s.outcome}`)}
                  </Badge>
                  <span className="text-ink-3">{t('admin.workflows.attempt', { n: s.attempt })}</span>
                  <span className="text-ink-3">{formatDate(s.finishedAt)}</span>
                </p>
                {s.code && <p className="mt-1 text-ink-2">{w.code(s.code)}</p>}
                <StepDetail detail={s.detail} />
              </li>
            ))}
          </ol>
        )}
        {r.actions.length > r.stepIndex && r.status !== 'failed' && (
          <p className="mt-2 text-xs text-ink-3">
            {t('admin.workflows.remaining', {
              list: r.actions
                .slice(r.stepIndex)
                .map((a) => w.action(a.type))
                .join(' → '),
            })}
          </p>
        )}
      </section>
    </PageFrame>
  );
}

function StepDetail({ detail }: { detail: Record<string, unknown> }) {
  const { t } = useWorkflowText();
  const { formatDate } = useI18n();
  const parts: string[] = [];
  if (Array.isArray(detail.issues) && detail.issues.length)
    parts.push(
      t('admin.workflows.detail.issues', {
        list: (detail.issues as string[]).map((c) => t(`admin.seo.issueNames.${c}`)).join(', '),
      }),
    );
  if (typeof detail.recipients === 'number')
    parts.push(t('admin.workflows.detail.recipients', { count: detail.recipients }));
  if (typeof detail.sent === 'number')
    parts.push(t('admin.workflows.detail.sent', { sent: detail.sent, skipped: Number(detail.skipped ?? 0) }));
  if (typeof detail.publishAt === 'string')
    parts.push(t('admin.workflows.detail.publishAt', { date: formatDate(detail.publishAt) }));
  if (detail.publishAt === null) parts.push(t('admin.workflows.detail.publishedNow'));
  if (typeof detail.inserted === 'number')
    parts.push(
      t('admin.workflows.detail.merchant', {
        inserted: detail.inserted,
        deleted: Number(detail.deleted ?? 0),
        unchanged: Number(detail.unchanged ?? 0),
      }),
    );
  if (typeof detail.reason === 'string') parts.push(t(`errors.${detail.reason}`));
  if (typeof detail.status === 'string' && detail.invoiceId === undefined)
    parts.push(
      t('admin.workflows.detail.orderStatus', { status: t(`admin.orders.statuses.${detail.status}`) }),
    );
  if (typeof detail.invoiceId === 'string')
    parts.push(
      t('admin.workflows.detail.invoice', {
        status: t(`admin.workflows.invoiceStatuses.${String(detail.status ?? 'draft')}`),
      }),
    );
  if (typeof detail.permission === 'string')
    parts.push(t('admin.workflows.detail.permission', { name: t(`permissions.${detail.permission}`) }));
  if (!parts.length) return null;
  return (
    <ul className="mt-1 list-disc ps-5 text-xs text-ink-3">
      {parts.map((p, i) => (
        <li key={i}>{p}</li>
      ))}
    </ul>
  );
}
